// src/hooks/useFeatureAccess.ts — what this organisation is entitled to.
//
// One fetch of GET /v1/feature-locks/effective, shared by everything that gates
// on it: the sidebar, the mobile nav and PrivateRoute all ask the same question
// on the same render, and three independent requests for one answer would be
// both wasteful and capable of disagreeing mid-flight.
//
// The cache is keyed by organization id, so switching organisations re-asks
// rather than showing the previous tenant's entitlements — which would be the
// one failure mode worse than not caching at all.

import { useEffect, useState } from 'react';
import { useAuth } from './useAuth';
import { featureLocksService } from 'api/services/featureLocks.service';
import type { EffectiveFeatureMap } from 'components/Layout/navVisibility';

type Entry = { features?: EffectiveFeatureMap; inflight?: Promise<void> };

const cache = new Map<string, Entry>();

/** Exported for tests and for logout, where stale entitlements must not survive. */
export function clearFeatureAccessCache(): void {
  cache.clear();
}

/** The server refused the session's organisation itself, not a transient failure. */
export function isStaleOrganization(res: any, user: any): boolean {
  return !!res && res.success === false
      && [res.message, res.error].some((m: any) => typeof m === 'string' && m.includes('No organization on this session'))
      && Array.isArray(user?.organizations) && user.organizations.length > 0;
}

export function useFeatureAccess(): EffectiveFeatureMap | undefined {
  const { user, currentOrganization, switchOrganization } = useAuth() as any;
  const orgId = (currentOrganization as any)?.id ?? (user as any)?.organization_id ?? null;

  const [features, setFeatures] = useState<EffectiveFeatureMap | undefined>(
    orgId ? cache.get(orgId)?.features : undefined,
  );

  useEffect(() => {
    // No session yet: return undefined, which navVisibility treats as "changes
    // nothing". The nav must not blank out while auth is still settling.
    if (!orgId || !user) {
      setFeatures(undefined);
      return;
    }

    let alive = true;
    const entry = cache.get(orgId) ?? {};
    cache.set(orgId, entry);

    if (entry.features) {
      setFeatures(entry.features);
      return;
    }

    if (!entry.inflight) {
      entry.inflight = featureLocksService.effective().then(res => {
        // A refusal or an error leaves `features` undefined on purpose. Hiding
        // the whole nav because one request failed would be indistinguishable,
        // to the person looking at it, from having been suspended.
        if (res.success && res.data?.features) {
          entry.features = res.data.features as EffectiveFeatureMap;
        } else if (isStaleOrganization(res, user)) {
          // The browser still acts as an organisation this user can no longer
          // reach (e.g. it was moved to another provider). Every request then
          // runs as that org, and a refused lock lookup would read as "no locks"
          // (14 Oct 2026: Xtreme's WhatsApp hold vanished for a user left on
          // ECOCVA after it moved under Teeoff). Go back to their own org.
          const home = (user as any).organizations[0];
          if (switchOrganization && home && localStorage.getItem('currentOrganizationId') !== home.id) {
            switchOrganization(home);
          }
        }
        entry.inflight = undefined;
      }).catch(() => {
        entry.inflight = undefined;
      });
    }

    entry.inflight.then(() => {
      if (alive) setFeatures(cache.get(orgId)?.features);
    });

    return () => { alive = false; };
  }, [orgId, user]);

  return features;
}
